import { useQueryClient } from '@tanstack/react-query';
import { api, orgID, post, useData, useList, useSession } from '@yingbaba/api';
import { useWorkspaceUI } from '@yingbaba/auth';
import { ArrowUpRight, Bell, Heart, Plus, Trash2 } from '@yingbaba/icons';
import type { Demand, Favorite, Proposal, Provider } from '@yingbaba/types';
import {
  ActionDialog,
  Button,
  ButtonLink,
  DemandCard,
  ErrorNotice,
  IconButton,
  Image,
  Modal,
  Notice,
  PageTitle,
  Pagination,
  QueryState,
  Status,
  useToast,
} from '@yingbaba/ui';
import { collaborationOnly, date, datetime, money, siteURL } from '@yingbaba/utils';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

type MarketplaceOpportunity = {
  category: string;
  demand_30_days: number;
  demand_prev_30_days: number;
  growth_percent: number;
  provider_count: number;
  demand_per_provider_tenths: number;
  opportunity_score: number;
  relevant: boolean;
  reasons: string[];
  actions: string[];
};
type MarketplaceOpportunities = { sample_days: number; items: MarketplaceOpportunity[] };
type DemandSubscription = {
  id: string;
  category: string;
  city: string;
  tags: string;
  enabled: boolean;
};

export function DemandList() {
  const { capability } = useWorkspaceUI();
  const [page, setPage] = useState(1);
  const [opBusy, setOpBusy] = useState('');
  const [opError, setOpError] = useState<unknown>(null);
  const provider = capability === 'provider';
  const q = useList<Demand>(`${provider ? '' : '/workspace'}/demands?page=${page}&page_size=9`);
  const proposals = useList<Proposal>('/workspace/proposals', provider && !collaborationOnly);
  const opportunities = useData<MarketplaceOpportunities>(
    '/workspace/marketplace-opportunities',
    provider,
  );
  const subscriptions = useData<DemandSubscription[]>('/workspace/demand-subscriptions', provider);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  async function createOpportunityDraft(category: string, kind: 'service' | 'case') {
    const key = `${kind}:${category}`;
    setOpBusy(key);
    setOpError(null);
    try {
      await post(
        `/workspace/marketplace-opportunities/${encodeURIComponent(category)}/${kind}-draft`,
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['/service-packages', orgID()] }),
        qc.invalidateQueries({ queryKey: ['/workspace/cases', orgID()] }),
      ]);
      toast(
        kind === 'service' ? '服务草稿已创建，请完善后发布' : '案例草稿已创建，请完善后提交审核',
      );
      navigate('/provider');
    } catch (e) {
      setOpError(e);
    } finally {
      setOpBusy('');
    }
  }

  async function toggleSubscription(category: string, enabled: boolean) {
    const key = `subscription:${category}`;
    setOpBusy(key);
    setOpError(null);
    try {
      await post('/workspace/demand-subscriptions', { category, city: '', tags: '', enabled });
      await qc.invalidateQueries({ queryKey: ['/workspace/demand-subscriptions', orgID()] });
      toast(enabled ? '已订阅同类需求' : '已取消需求订阅');
    } catch (e) {
      setOpError(e);
    } finally {
      setOpBusy('');
    }
  }
  return (
    <>
      <PageTitle
        title={provider ? '发现新的合作机会' : '我的需求'}
        description={
          provider
            ? '用专业的作品和清晰的沟通，找到下一次合作。'
            : '发布清楚的需求，与合适的团队建立联系。'
        }
        action={
          <ButtonLink to={siteURL('www', provider ? '/demands' : '/publish')}>
            <Plus size={16} />
            {provider ? '发现更多项目' : '发布需求'}
          </ButtonLink>
        }
      />
      {provider && opportunities.data?.items.length ? (
        <section className="panel" style={{ marginBottom: 24 }}>
          <div className="row spread wrap">
            <div>
              <h3>市场机会</h3>
              <p className="muted small">
                基于近 60
                天平台公开需求与当前认证团队供给，仅用于判断平台内机会，不代表行业总体规模。
              </p>
            </div>
            <ButtonLink to="/provider" variant="secondary">
              优化我的服务
            </ButtonLink>
          </div>
          <div className="grid-3" style={{ marginTop: 18 }}>
            {opportunities.data.items.slice(0, 6).map((item) => (
              <article className="surface" key={item.category} style={{ padding: 18 }}>
                <div className="row spread">
                  <strong>{item.category}</strong>
                  {item.relevant ? <span className="badge">与你相关</span> : null}
                </div>
                <p className="small" style={{ marginTop: 10 }}>
                  近 30 天 {item.demand_30_days} 个需求 · {item.provider_count} 个认证团队
                </p>
                <p className="small muted">
                  {item.growth_percent > 0
                    ? `较前 30 天增长 ${item.growth_percent}%`
                    : item.growth_percent < 0
                      ? `较前 30 天下降 ${Math.abs(item.growth_percent)}%`
                      : '较前 30 天基本持平'}
                </p>
                <div style={{ marginTop: 12 }}>
                  {item.reasons.slice(0, 2).map((reason) => (
                    <p className="small" key={reason}>
                      • {reason}
                    </p>
                  ))}
                </div>
                {item.actions[0] ? (
                  <p className="small muted" style={{ marginTop: 12 }}>
                    建议：{item.actions[0]}
                  </p>
                ) : null}
                <div className="row wrap" style={{ marginTop: 14 }}>
                  <Button
                    type="button"
                    variant="secondary"
                    loading={opBusy === `service:${item.category}`}
                    disabled={!!opBusy && opBusy !== `service:${item.category}`}
                    onClick={() => createOpportunityDraft(item.category, 'service')}
                  >
                    创建服务草稿
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    loading={opBusy === `case:${item.category}`}
                    disabled={!!opBusy && opBusy !== `case:${item.category}`}
                    onClick={() => createOpportunityDraft(item.category, 'case')}
                  >
                    创建案例草稿
                  </Button>
                  {(() => {
                    const current = subscriptions.data?.find(
                      (subscription) =>
                        subscription.category === item.category &&
                        !subscription.city &&
                        !subscription.tags,
                    );
                    const enabled = current?.enabled ?? false;
                    return (
                      <Button
                        type="button"
                        variant="ghost"
                        loading={opBusy === `subscription:${item.category}`}
                        disabled={!!opBusy && opBusy !== `subscription:${item.category}`}
                        onClick={() => toggleSubscription(item.category, !enabled)}
                      >
                        <Bell size={14} />
                        {enabled ? '取消订阅' : '订阅同类需求'}
                      </Button>
                    );
                  })()}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
      <ErrorNotice error={opError || subscriptions.error} />
      <QueryState query={q} empty={q.data?.items.length === 0}>
        <div className="grid-3">
          {q.data?.items.map((d) => (
            <DemandCard key={d.id} item={d} />
          ))}
        </div>
      </QueryState>
      <Pagination page={page} total={q.data?.total || 0} size={9} onPage={setPage} />
      {provider && proposals.data?.items.length ? (
        <section className="panel" style={{ marginTop: 30 }}>
          <h3>我的报价记录</h3>
          {proposals.data.items.map((p) => (
            <div className="list-item" key={p.id}>
              <div className="grow">
                <Link to={'/demands/' + p.demand_id}>
                  <strong>{p.summary.slice(0, 50)}</strong>
                </Link>
                <p>
                  第 {p.version} 版 · {money(p.amount_minor)} · {p.days} 天
                </p>
              </div>
              <Status value={p.status} />
            </div>
          ))}
        </section>
      ) : null}
    </>
  );
}
type Match = { provider: Provider; score: number; reasons: string[] };
export { Team } from './Team';
export { Notifications } from './Notifications';
export function Favorites() {
  const [page, setPage] = useState(1);
  const q = useList<Favorite>(`/workspace/favorites?page=${page}&page_size=12`);
  return (
    <>
      <PageTitle title="我的收藏" description="留住打动你的团队、作品与资源。" />
      <QueryState query={q} empty={!q.data?.items.length}>
        <div className="panel">
          {q.data?.items.map((f) => (
            <FavoriteRow key={f.id} item={f} />
          ))}
        </div>
      </QueryState>
      <Pagination page={page} size={12} total={q.data?.total || 0} onPage={setPage} />
    </>
  );
}
type FavoriteDetail = {
  title?: string;
  name?: string;
  cover?: string;
  category?: string;
  provider?: FavoriteDetail;
};
function FavoriteRow({ item }: { item: Favorite }) {
  const detail = useData<FavoriteDetail>(`/${item.kind}/${item.entity_id}`);
  const value = detail.data?.provider || detail.data;
  const query = useQueryClient(),
    toast = useToast();
  const [busy, setBusy] = useState(false);
  const kind = (
    {
      providers: '创作团队',
      demands: '拍片需求',
      resources: '影视资源',
      cases: '精选案例',
    } as Record<string, string>
  )[item.kind];
  async function remove() {
    setBusy(true);
    try {
      await api(`/favorites/${item.kind}/${item.entity_id}`, { method: 'DELETE' });
      await query.invalidateQueries();
      toast('已取消收藏');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="list-item">
      {value?.cover ? (
        <Image src={value.cover} alt="" className="favorite-thumbnail" />
      ) : (
        <span className="icon-tile">
          <Heart size={19} />
        </span>
      )}
      <div className="grow">
        {value ? (
          <a className="link" href={siteURL('www', `/${item.kind}/${item.entity_id}`)}>
            <strong>{value.name || value.title}</strong>
            <ArrowUpRight size={14} />
          </a>
        ) : (
          <strong>{detail.isPending ? '正在读取收藏…' : '内容已下架或暂时不可访问'}</strong>
        )}
        <p>
          {kind} · {date(item.created_at)} 收藏
        </p>
      </div>
      <IconButton label="取消收藏" onClick={remove} disabled={busy}>
        <Trash2 size={17} />
      </IconButton>
    </div>
  );
}
